import { isUser } from "@/stores/user";
import { type RouteLocation, type RouteLocationRaw } from "vue-router";

export function useOnboardingCheck(): {
  checkOnboarding: (to?: Partial<RouteLocation>) => RouteLocationRaw | false;
} {
  const userStore = useUserStore();
  const onboardingRouteNames = new Set([
    "onboarding-entry",
    "onboarding-step",
    "re-onboarding",
  ]);

  const checkOnboarding = (
    to?: Partial<RouteLocation>
  ): RouteLocationRaw | false => {
    if (!isUser(userStore.user) || userStore.isIncognito) {
      return false;
    }

    if (isOnboardingRoute(to)) {
      return false;
    }

    if (userStore.user.needsFirstOnboarding) {
      return redirectToOnboarding();
    } else if (
      userStore.user.needsReOnboarding &&
      userStore.user.canQuestionReOnboarding &&
      to?.name === "home"
    ) {
      return redirectToReonboarding();
    }

    return false;
  };

  const redirectToOnboarding = (): RouteLocationRaw | false => {
    if (!isUser(userStore.user)) {
      return false;
    }

    return buildRedirectRoute("onboarding-entry");
  };

  const redirectToReonboarding = (): RouteLocationRaw | false => {
    if (!isUser(userStore.user)) {
      return false;
    }

    return buildRedirectRoute("re-onboarding");
  };

  const buildRedirectRoute = (
    name: "onboarding-entry" | "re-onboarding"
  ): RouteLocationRaw => {
    const redirectTo = redirectToValue();
    if (!redirectTo) return { name };

    return {
      name,
      query: {
        redirect_to: redirectTo,
      },
    };
  };

  const redirectToValue = (): string | undefined => {
    const currentLocation = new URL(window.location.href, window.location.href);
    if (isOnboardingPath(currentLocation.pathname)) {
      return undefined;
    }

    return window.location.href;
  };

  const isOnboardingRoute = (to?: Partial<RouteLocation>): boolean => {
    const routeName = to?.name?.toString();
    if (routeName && onboardingRouteNames.has(routeName)) {
      return true;
    }

    if (typeof to?.path === "string") {
      return isOnboardingPath(to.path);
    }

    return false;
  };

  const isOnboardingPath = (path: string): boolean =>
    path === "/onboarding/edit" ||
    path.startsWith("/onboarding/") ||
    path === "/re_onboarding";

  return { checkOnboarding };
}
